import { BuyBackHistory, BuyBackItem } from './types'

const BUYBACK_STORAGE_KEY = 'buyback_history'

export const getBuyBackHistory = (walletAddress: string): BuyBackHistory | null => {
  try {
    if (typeof window === 'undefined') return null

    const stored = localStorage.getItem(`${BUYBACK_STORAGE_KEY}_${walletAddress.toLowerCase()}`)
    if (!stored) return null

    const history = JSON.parse(stored) as BuyBackHistory
    return history
  } catch (error) {
    return null
  }
}

/**
 * Save buyback history for a wallet address
 */
export const saveBuyBackHistory = (walletAddress: string, history: BuyBackHistory): void => {
  try {
    if (typeof window === 'undefined') return

    localStorage.setItem(
      `${BUYBACK_STORAGE_KEY}_${walletAddress.toLowerCase()}`,
      JSON.stringify(history)
    )
  } catch (error) {
    // Error saving buyback history
  }
}

/**
 * Add a new buyback item to history
 */
export const addBuyBackItem = (
  walletAddress: string,
  item: Omit<BuyBackItem, 'id' | 'timestamp' | 'status'>
): BuyBackItem => {
  const history = getBuyBackHistory(walletAddress) || {
    walletAddress: walletAddress.toLowerCase(),
    items: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }

  const newItem: BuyBackItem = {
    ...item,
    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: Date.now(),
  }

  history.items.push(newItem)
  history.updatedAt = Date.now()

  if (history.items.length > 100) {
    history.items = history.items.slice(-100)
  }

  saveBuyBackHistory(walletAddress, history)
  return newItem
}

/**
 * Update buyback item txHash (status is not persisted)
 */
export const updateBuyBackItemTxHash = (
  walletAddress: string,
  itemId: string,
  txHash: string
): void => {
  const history = getBuyBackHistory(walletAddress)
  if (!history) return

  const item = history.items.find(i => i.id === itemId)
  if (!item) return

  item.txHash = txHash
  history.updatedAt = Date.now()
  saveBuyBackHistory(walletAddress, history)
}

/**
 * Remove buyback item
 */
export const removeBuyBackItem = (walletAddress: string, itemId: string): void => {
  const history = getBuyBackHistory(walletAddress)
  if (!history) return

  history.items = history.items.filter(i => i.id !== itemId)
  history.updatedAt = Date.now()
  saveBuyBackHistory(walletAddress, history)
}

/**
 * Clear all buyback history for a wallet
 */
export const clearBuyBackHistory = (walletAddress: string): void => {
  try {
    if (typeof window === 'undefined') return
    localStorage.removeItem(`${BUYBACK_STORAGE_KEY}_${walletAddress.toLowerCase()}`)
  } catch (error) {
    // Error clearing buyback history
  }
}

/**
 * Get all buyback items (all are considered pending since status is not persisted)
 */
export const getPendingBuyBackItems = (walletAddress: string): BuyBackItem[] => {
  const history = getBuyBackHistory(walletAddress)
  if (!history) return []

  return history.items
}

/**
 * Format USD amount for display
 */
export const formatUSDAmount = (amount: number): string => {
  if (amount < 0.00001) return '< $0.00001'
  if (amount < 1) return `$${amount.toFixed(4)}`
  return `$${amount.toFixed(2)}`
}

/**
 * Calculate total USD value of all buybacks
 */
export const getTotalPendingBuyBackUSD = (walletAddress: string): number => {
  const items = getPendingBuyBackItems(walletAddress)
  return items.reduce((total, item) => total + item.buybackAmountUSD, 0)
}

